import { useEffect } from 'react';
import { EXPERIMENT_NAME_AB, EXPERIMENT_NAME_AA } from '../../common/utils/constants';
import { useExperimentEligibility } from '../check-experiment-eligibility';
import { useCanShowExperiment } from '../check-experiment-eligibility/useCanShowExperiment';
import { useExperimentEnrollment } from '../check-experiment-eligibility/useExperimentEnrollment';
import { useFireExposureEvents } from '../check-experiment-eligibility/useFireExposureEvents';
import { useTrialDetails } from '../check-experiment-eligibility/useTrialDetails';
import { setHasSeenModal } from '../set-modal-local-storage/setHasSeenModal';

export interface ABExperimentProps {
	isExperimentEnabled: boolean;
	extraAnalytics: object;
	experimentName: string;
}

const useExperiment = ({
	isExperimentEnabled,
	extraAnalytics,
	experimentName,
}: ABExperimentProps) => {
	const { isEligible, isUrgent } = useExperimentEligibility(experimentName);
	const { trialDaysLeft } = useTrialDetails();

	const { cohort, isEnrolled, isTreatment } = useExperimentEnrollment({
		experimentName,
		skip: !isExperimentEnabled,
	});

	const shouldTryShowExperiment = isExperimentEnabled && isEnrolled && isEligible;

	const { canShowExperiment } = useCanShowExperiment({ skip: !shouldTryShowExperiment });

	const { fireExposureEvents } = useFireExposureEvents({
		skip: !canShowExperiment,
		experimentName,
		extraAnalytics: {
			...extraAnalytics,
			isUrgent,
			trialDaysLeft,
		},
	});

	useEffect(() => {
		// this is a no-op if skip condition passed to useFireExposureEvents is true
		fireExposureEvents();
	}, [fireExposureEvents]);

	if (!canShowExperiment) {
		return {
			cohort,
			renderTreatment: false,
		};
	}

	setHasSeenModal(experimentName, isUrgent);

	return {
		cohort,
		renderTreatment: isTreatment,
	};
};

export const useJiraLossAversionABExperiment = ({
	isExperimentEnabled,
	extraAnalytics,
}: {
	isExperimentEnabled: boolean;
	extraAnalytics: object;
}) =>
	useExperiment({
		isExperimentEnabled,
		extraAnalytics,
		experimentName: EXPERIMENT_NAME_AB,
	});

export const useJiraLossAversionAAExperiment = ({
	isExperimentEnabled,
	extraAnalytics,
}: {
	isExperimentEnabled: boolean;
	extraAnalytics: object;
}) =>
	useExperiment({
		isExperimentEnabled,
		extraAnalytics,
		experimentName: EXPERIMENT_NAME_AA,
	});
